import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutKey {
  mac?: string; // macOS symbol/key
  win?: string; // Windows key
  common?: string; // Same key for both OS
}

interface Shortcut {
  keys: ShortcutKey[];
  description: string;
  category: 'timer' | 'music' | 'navigation' | 'chat';
}

const shortcuts: Shortcut[] = [
  // Timer
  {
    keys: [{ mac: '⌃', win: 'Alt' }, { common: 'S' }],
    description: 'Start/Pause/Resume timer',
    category: 'timer',
  },
  {
    keys: [{ mac: '⌃', win: 'Alt' }, { common: 'R' }],
    description: 'Reset timer',
    category: 'timer',
  },

  // Music
  { keys: [{ common: 'Space' }], description: 'Play/Pause music', category: 'music' },

  // Navigation
  {
    keys: [{ mac: '⌃', win: 'Alt' }, { common: 'O' }],
    description: 'Open Settings',
    category: 'navigation',
  },
  {
    keys: [{ mac: '⌃', win: 'Alt' }, { common: 'I' }],
    description: 'Open User Info',
    category: 'navigation',
  },
  { keys: [{ common: 'Esc' }], description: 'Close modals', category: 'navigation' },

  // Chat
  {
    keys: [{ mac: '⌃', win: 'Alt' }, { common: 'C' }],
    description: 'Open/Close Chat',
    category: 'chat',
  },
];

const categoryNames = {
  timer: 'Timer',
  music: 'Music',
  navigation: 'Navigation',
  chat: 'Chat',
};

// Key button component for consistent styling
function KeyBadge({
  children,
  variant = 'common',
}: {
  children: React.ReactNode;
  variant?: 'mac' | 'win' | 'common';
}) {
  const baseStyles =
    'inline-flex items-center justify-center min-w-[28px] px-2 py-1 text-[11px] font-medium rounded-md shadow-sm border';

  const variantStyles = {
    mac: 'bg-[#2A2A2E] text-gray-200 border-[#3A3A3E]',
    win: 'bg-[#1E3A5F] text-blue-200 border-[#2A4A6F]',
    common: 'bg-[#1F1F22] text-gray-300 border-white/10',
  };

  return <kbd className={`${baseStyles} ${variantStyles[variant]}`}>{children}</kbd>;
}

// Renders the keys for a shortcut with OS-specific styling
function ShortcutKeys({ keys }: { keys: ShortcutKey[] }) {
  // Check if this shortcut has OS-specific keys
  const hasOsSpecific = keys.some((k) => k.mac || k.win);

  if (!hasOsSpecific) {
    // Simple case: just common keys
    return (
      <div className="flex items-center gap-1.5">
        {keys.map((key, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <KeyBadge variant="common">{key.common}</KeyBadge>
            {i < keys.length - 1 && <span className="text-gray-600 text-[10px]">+</span>}
          </span>
        ))}
      </div>
    );
  }

  // OS-specific keys: show Mac and Windows variants
  return (
    <div className="flex items-center gap-3">
      {/* macOS keys */}
      <div className="flex items-center gap-1">
        {keys.map((key, i) => (
          <span key={`mac-${i}`} className="flex items-center gap-1">
            {key.mac && <KeyBadge variant="mac">{key.mac}</KeyBadge>}
            {key.common && <KeyBadge variant="common">{key.common}</KeyBadge>}
            {i < keys.length - 1 && <span className="text-gray-600 text-[10px]">+</span>}
          </span>
        ))}
      </div>

      <span className="text-gray-600 text-[10px]">/</span>

      {/* Windows keys */}
      <div className="flex items-center gap-1">
        {keys.map((key, i) => (
          <span key={`win-${i}`} className="flex items-center gap-1">
            {key.win && <KeyBadge variant="win">{key.win}</KeyBadge>}
            {key.common && <KeyBadge variant="common">{key.common}</KeyBadge>}
            {i < keys.length - 1 && <span className="text-gray-600 text-[10px]">+</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const categories = ['timer', 'music', 'navigation', 'chat'] as const;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen && e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="relative w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh] shadow-2xl rounded-xl border border-white/10 bg-[#0F0F10]"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-white/[0.02]">
              <h2 className="text-lg font-medium text-white flex items-center gap-2">
                Keyboard Shortcuts
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-white/10 transition-colors text-white/40 hover:text-white"
                aria-label="Close shortcuts"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
              <div className="p-2 space-y-6">
                {categories.map((category) => {
                  const categoryShortcuts = shortcuts.filter((s) => s.category === category);
                  if (categoryShortcuts.length === 0) return null;

                  return (
                    <div key={category} className="px-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 px-2">
                        {categoryNames[category]}
                      </h3>
                      <div className="space-y-1">
                        {categoryShortcuts.map((shortcut, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/[0.03] transition-colors group"
                          >
                            <span className="text-gray-300 text-sm font-medium group-hover:text-white transition-colors">
                              {shortcut.description}
                            </span>
                            <ShortcutKeys keys={shortcut.keys} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-white/[0.02] border-t border-white/5">
              <p className="text-[11px] text-center text-gray-500">
                Press <span className="text-gray-400">Esc</span> to close
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
